import React, { useEffect, useState } from "react";
import { CancelOutlined, Check, Close } from "@mui/icons-material";
import { Alert, Box, Button, CircularProgress, TextField, Typography } from "@mui/material";
import LabeledField from "../../components/form/LabeledField";
import { PifApplicantSummary } from "./pifDetailsModel";

interface WorkflowApprovalStageProps {
  reference: string;
  definitionName: string;
  /** The canvas name, which the prototype repeats as the section heading above the decision. */
  stageName: string;
  approver: string;
  /** Only the PIF flow collects these, so the case summary is omitted for every other
   *  definition rather than rendered with empty values. */
  applicant: PifApplicantSummary | null;
  onAccept: () => void;
  onReject: (reason: string) => void;
  onCancel: (reason: string) => void;
  /** A real case's resolved approvers. An empty list means none could be resolved, which
   *  disables the decision (a mis-configured stage). Undefined while they load. */
  approverNames?: string[];
  /** Decisions already recorded on this stage — the attest stamp is name and time (no IP in v1). */
  stamps?: WorkflowApprovalStamp[];
  /** e.g. "1 of 2 required approvals recorded", while the counting model still waits. */
  progressNote?: string;
  /** A decision is in flight: every action is disabled. */
  busy?: boolean;
  /** The decision is not available (no approver, a conflict to reload, not yours to sign). */
  disabled?: boolean;
  banner?: React.ReactNode;
  /** The engine refuses a cancel without a reason, so a real case asks for one. */
  requireCancelReason?: boolean;
}

export interface WorkflowApprovalStamp {
  name: string;
  decision: "accept" | "reject" | "cancel";
  at: string;
}

const STAMP_VERBS: Record<WorkflowApprovalStamp["decision"], string> = {
  accept: "Accepted",
  reject: "Rejected",
  cancel: "Cancelled",
};

const PENDING_LABELS: Record<WorkflowApprovalStamp["decision"], string> = {
  accept: "Accepting…",
  reject: "Rejecting…",
  cancel: "Cancelling…",
};

const Spinner = <CircularProgress size={16} color="inherit" aria-hidden="true" />;

const WorkflowApprovalStage: React.FC<WorkflowApprovalStageProps> = ({
  reference,
  definitionName,
  stageName,
  approver,
  applicant,
  onAccept,
  onReject,
  onCancel,
  approverNames,
  stamps = [],
  progressNote,
  busy = false,
  disabled = false,
  banner,
  requireCancelReason = false,
}) => {
  const [note, setNote] = useState("");
  const [noteError, setNoteError] = useState("");
  // The button the user pressed, so its own spinner shows while that decision is in flight. Cleared
  // once the stage is no longer busy, so a later reload never re-shows it.
  const [clicked, setClicked] = useState<WorkflowApprovalStamp["decision"] | null>(null);
  useEffect(() => {
    if (!busy) setClicked(null);
  }, [busy]);
  const pending = busy ? clicked : null;

  const noApprover = approverNames !== undefined && approverNames.length === 0;
  const actionsDisabled = busy || disabled || noApprover;

  const handleAccept = () => {
    setClicked("accept");
    onAccept();
  };

  const handleNoteChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setNote(event.target.value);
    setNoteError("");
  };

  const handleReject = () => {
    const reason = note.trim();
    if (!reason) {
      setNoteError("Enter a reason before rejecting this case.");
      return;
    }
    setClicked("reject");
    onReject(reason);
  };

  const handleCancel = () => {
    const reason = note.trim();
    if (requireCancelReason && !reason) {
      setNoteError("Enter a reason before cancelling this case.");
      return;
    }
    setClicked("cancel");
    onCancel(reason);
  };

  return (
    <>
      <Alert severity="info" className="workflow-run__alert--flush">
        You are asked to review this {definitionName} case. Approval records your name, date/time,
        and IP as an attest-style electronic signature.
      </Alert>

      <Box className="workflow-run__case-summary" aria-label="Case summary">
        <span>
          Case <strong>{reference}</strong>
        </span>
        {applicant && (
          <>
            <span>
              Applicant <strong>{applicant.name || "—"}</strong>
            </span>
            {/* P6: approvers sign off a hire for a specific position, so it is always named. */}
            <span>
              Position <strong>{applicant.position || "—"}</strong>
            </span>
            <span>
              Department <strong>{applicant.department || "—"}</strong>
            </span>
            <span>
              Employment <strong>{applicant.employmentType || "—"}</strong>
            </span>
            <span>
              First working day <strong>{applicant.firstWorkingDay || "—"}</strong>
            </span>
            <span>
              Rate <strong>{applicant.rate ? `$${applicant.rate}/hr` : "—"}</strong>
            </span>
          </>
        )}
      </Box>

      <Typography component="h3" className="workflow-run__section-title">
        {stageName}
      </Typography>
      <LabeledField controlId="workflow-approver" label="Approver" badge="Read-only">
        <TextField
          id="workflow-approver"
          size="small"
          fullWidth
          value={approverNames ? approverNames.join(", ") : approver}
          disabled
        />
      </LabeledField>
      {noApprover && (
        <Alert severity="warning" className="workflow-run__alert">
          No approver assigned — contact HR.
        </Alert>
      )}
      {stamps.map((stamp) => (
        <Typography key={`${stamp.name}-${stamp.at}`} className="workflow-run__hint">
          <strong>{STAMP_VERBS[stamp.decision]}</strong> by {stamp.name} · {stamp.at}
        </Typography>
      ))}
      {progressNote && <Typography className="workflow-run__hint">{progressNote}</Typography>}
      <LabeledField
        controlId="workflow-approval-note"
        label={requireCancelReason ? "Note (required to reject or cancel)" : "Note (required if rejecting)"}
        className="workflow-run__approval-note"
        error={noteError || undefined}
      >
        <TextField
          id="workflow-approval-note"
          placeholder="Optional for accept or cancel"
          size="small"
          fullWidth
          multiline
          minRows={3}
          value={note}
          onChange={handleNoteChange}
          error={Boolean(noteError)}
        />
      </LabeledField>

      {banner}

      <Box className="workflow-run__actions" aria-busy={pending !== null}>
        <Button
          className="workflow-run__decision workflow-run__decision--accept"
          variant="contained"
          startIcon={pending === "accept" ? Spinner : <Check />}
          onClick={handleAccept}
          disabled={actionsDisabled}
        >
          {pending === "accept" ? PENDING_LABELS.accept : "Accept"}
        </Button>
        <Button
          className="workflow-run__decision workflow-run__decision--reject"
          variant="contained"
          startIcon={pending === "reject" ? Spinner : <Close />}
          onClick={handleReject}
          disabled={actionsDisabled}
        >
          {pending === "reject" ? PENDING_LABELS.reject : "Reject"}
        </Button>
        <Button
          variant="outlined"
          color="secondary"
          startIcon={pending === "cancel" ? Spinner : <CancelOutlined />}
          onClick={handleCancel}
          disabled={actionsDisabled}
        >
          {pending === "cancel" ? PENDING_LABELS.cancel : "Cancel case"}
        </Button>
      </Box>
    </>
  );
};

export default WorkflowApprovalStage;
